import { Checkbox, Popover } from "antd";
import styled from "styled-components";
import { Icon } from "@/shared/components";
import type { ColumnSpec } from "../utils/columnVisibility";

interface Props {
	columns: ColumnSpec[];
	hidden: string[];
	onToggle: (key: string) => void;
	onReset: () => void;
}

/** The gear at the top right of an admin table: show or hide columns for this browser only. */
export const ColumnSettings = ({ columns, hidden, onToggle, onReset }: Props) => (
	<Popover
		trigger="click"
		placement="bottomRight"
		title="Columns"
		content={
			<List>
				{columns.map((column) => (
					<li key={column.key}>
						<Checkbox
							checked={!hidden.includes(column.key)}
							disabled={column.locked}
							onChange={() => onToggle(column.key)}
						>
							{column.title}
						</Checkbox>
					</li>
				))}
				<Footer>
					<ResetButton type="button" onClick={onReset} disabled={hidden.length === 0}>
						Show all
					</ResetButton>
					<Hint>Saved on this device</Hint>
				</Footer>
			</List>
		}
	>
		<GearButton type="button" aria-label="Choose columns" title="Choose columns">
			<Icon src="/icon/setting.svg" size={20} />
		</GearButton>
	</Popover>
);

/** Wraps a wide table so it scrolls left to right inside its own container, never the page. */
export const ScrollTable = styled.div<{ $minWidth: number }>`
	width: 100%;
	overflow-x: auto;
	padding-bottom: 4px;
	/* The shared table wrapper clips overflow; size it to the visible columns so this container scrolls instead. */
	> * {
		min-width: ${({ $minWidth }) => $minWidth}px;
		overflow: visible !important;
	}
	.ant-table {
		min-width: ${({ $minWidth }) => $minWidth}px;
	}
`;

const List = styled.ul`
	list-style: none;
	margin: 0;
	padding: 4px 0 0;
	min-width: 220px;
	li {
		padding: 4px 0;
	}
`;
const Footer = styled.li`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	margin-top: 6px;
	padding-top: 8px !important;
	border-top: 1px solid #ebeefd;
`;
const ResetButton = styled.button`
	border: 0;
	background: transparent;
	padding: 0;
	color: #3a0ca3;
	font-size: 13px;
	font-weight: 600;
	cursor: pointer;
	&:disabled {
		color: #b0b0b0;
		cursor: default;
	}
`;
const Hint = styled.span`
	color: #9b9b9b;
	font-size: 11px;
`;
const GearButton = styled.button`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 40px;
	height: 40px;
	border: 1px solid #cdd4f6;
	border-radius: 8px;
	background: #fff;
	cursor: pointer;
	flex-shrink: 0;
	&:hover,
	&:focus-visible {
		border-color: #9fadf1;
		outline: none;
	}
`;
